import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Step,
    StepLabel,
    Stepper,
    Stack,
    TextField,
    Typography,
    MenuItem,
} from "@mui/material";
import {createDeferredAction, createScript} from "../api/AiAgentApi";
import CreateScriptForm from "./CreateScriptForm";
import {useNavigate} from "react-router-dom";

/** map enum → подсказка по типу */
const TYPE_HINT = {
    STRING: "строка",
    INTEGER: "целое число",
    DOUBLE: "число",
    BOOLEAN: "true/false",
    DATETIME: "ISO-8601 datetime",
    UUID: "UUID",
};

/**
 * Wizard со Stepper:
 *  1) Создание скрипта (<CreateScriptForm/>)
 *  2) Параметры и запуск (тип + остальные поля + создание отложенного действия)
 */
export default function CreateDeferredActionWizard() {
    const navigate = useNavigate();

    // === Stepper logic ===
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Создать скрипт", "Создать отложенное действие"];

    const handleNext = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));
    const handleReset = () => setActiveStep(0);

    // === Данные из шага 1 (CreateScriptForm) ===
    const [script, setScript] = useState(null);     // детальная карточка скрипта (requestScriptBody)
    const [loadingScript] = useState(false);        // место под будущую подгрузку детали, если понадобится

    // === Данные шага 2: тип + поля для создания отложенного действия ===
    const [deferredType, setDeferredType] = useState("One-time");
    const [actionName, setActionName] = useState("");
    const [intervalMinutes, setIntervalMinutes] = useState("");
    const [externalValues, setExternalValues] = useState({}); // { scriptParamId: value }
    const [errors, setErrors] = useState({
        name: "",
        intervalMinutes: "",
        params: {}       // { scriptParamId: "error" }
    });

    const externalParams = useMemo(
        () => (script?.params || []).filter((p) => String(p.source) === "EXTERNAL"),
        [script]
    );

    function validateFinal() {
        const e = { name: "", intervalMinutes: "", params: {} };

        if (!actionName || actionName.trim().length === 0) {
            e.name = "Укажите название отложенного действия";
        }

        // Проверка обязательных внешних параметров
        for (const p of externalParams) {
            if (p.required) {
                const val = externalValues[p.id];
                if (val === undefined || val === null || String(val).trim() === "") {
                    e.params[p.id] = "Обязательное поле";
                }
            }
        }

        setErrors(e);
        const hasParamErrors = Object.keys(e.params).length > 0;
        return !e.name && !e.intervalMinutes && !hasParamErrors;
    }

    async function onSubmit() {
        if (!validateFinal()) return;

        // 1) создаём скрипт из того, что вернул CreateScriptForm
        const created = await createScript(script);

        // 2) собираем payload для отложенного действия
        const paramsPayload = externalParams.map((p) => ({
            scriptParamId: p.id,
            // сервер сам приведёт к нужному типу
            value: String(externalValues[p.id] ?? ""),
        }));

        const payload = {
            name: actionName.trim(),
            intervalMinutes: deferredType === "Regular" ? Number(1) : null,
            scriptId: created.id,
            params: paramsPayload,
        };

        await createDeferredAction(payload);
        navigate("/deferred-actions");
    }

    return (
        <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* ШАГ 1: Создание скрипта */}
            {activeStep === 0 && (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Шаг 1 — Создайте скрипт
                            </Typography>
                            <CreateScriptForm
                                requestScriptBody={script}
                                onNext={(scriptDraft) => {
                                    setScript(scriptDraft);
                                    handleNext();
                                }}
                            />
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* ШАГ 2: Параметры и запуск (объединённые «Выбрать тип» + «Параметры и запуск») */}
            {activeStep === 1 && (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <TextField
                                    select
                                    label="Тип отложенного действия"
                                    value={deferredType}
                                    onChange={(e) => setDeferredType(e.target.value)}
                                    fullWidth
                                    size="small"
                                    required
                                >
                                    <MenuItem value="One-time">Разовая</MenuItem>
                                    <MenuItem value="Regular">Регулярная</MenuItem>
                                </TextField>

                                <TextField
                                    label="Название отложенного действия"
                                    value={actionName}
                                    onChange={(e) => setActionName(e.target.value)}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    fullWidth
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleBack}>
                            Назад
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onSubmit}
                            disabled={!script}
                        >
                            Создать отложенное действие
                        </Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}
